import {ComboboxItem} from '@/components/ui/combobox-atoms/ComboboxAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {Label} from '@/components/ui/label/Label'
import {
    SelectComboboxInput,
    SelectComboboxList,
    SelectComboboxPopover,
    SelectItem,
    SelectTrigger,
    SelectVirtualList
} from '@/components/ui/select-atoms/SelectAtoms'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {useInfinitePatients} from '@/features/patients/services/useInfinitePatients'
import {PatientSmall} from '@/features/patients/types'
import {useTranslation} from '@/translations/i18n'
import {ComboboxProvider, SelectProvider} from '@ariakit/react'
import {forwardRef, useCallback, useRef, useState} from 'react'

interface PatientsMultiSelectProps {
    disabled?: boolean
    label?: string
    values: Pick<
        PatientSmall,
        'id' | 'fullName' | 'treatmentRequestId' | 'project' | 'neuromuscularDiagnosisId' | 'project'
    >[]
    onChange: (
        value: Pick<
            PatientSmall,
            'id' | 'fullName' | 'treatmentRequestId' | 'project' | 'neuromuscularDiagnosisId' | 'project'
        >[]
    ) => void
    placeholder?: string
    errorMessage?: string
    disabledOptions?: string[]
}

export const PatientsMultiSelect = forwardRef<HTMLSelectElement, PatientsMultiSelectProps>(
    ({label, values, onChange, placeholder, errorMessage, disabled}, ref) => {
        const {t} = useTranslation()
        const [isQueryEnabled, setIsQueryEnabled] = useState(false)
        const comboboxInputRef = useRef(null)
        const patientsQuery = useInfinitePatients(
            {
                limit: 10
            },
            {enabled: isQueryEnabled}
        )

        const handleChangePatient = useCallback(
            (selectedIds: string[]) => {
                // Get currently visible patients from the query that match selected IDs
                const visibleSelectedPatients = patientsQuery.remappedData.filter(patient =>
                    selectedIds.includes(patient.id)
                )

                // Get IDs that are selected but not visible in current query results
                const selectedButNotVisibleIds = selectedIds.filter(
                    id => !visibleSelectedPatients.some(p => p.id === id)
                )

                // Keep previously selected patients that are still in selectedIds but not in current view
                // We can safely assert these as PatientSmall since they were previously
                // accepted by the onChange function and must have had all required fields
                const previouslySelectedPatients = values.filter(patient =>
                    selectedButNotVisibleIds.includes(patient.id)
                )

                // Combine visible patients and previously selected patients
                const mergedPatients: Pick<
                    PatientSmall,
                    'id' | 'fullName' | 'treatmentRequestId' | 'project' | 'neuromuscularDiagnosisId' | 'project'
                >[] = [...visibleSelectedPatients, ...previouslySelectedPatients]

                onChange(mergedPatients)
            },
            [onChange, patientsQuery.remappedData, values]
        )

        return (
            <ComboboxProvider setValue={patientsQuery.onSearch} resetValueOnHide>
                <SelectProvider
                    value={values.map(value => value.id)}
                    setValue={handleChangePatient}
                    setOpen={isOpen => {
                        patientsQuery.onResetSearch(comboboxInputRef)
                        setIsQueryEnabled(isOpen)
                    }}
                >
                    <Flexbox direction="column" gap={1.5} fullWidth>
                        {label && <Label>{label}</Label>}
                        <SelectTrigger
                            ref={ref}
                            placeholder={placeholder}
                            fullWidth
                            value={values.map(value => value.id)}
                            displayValue={() => (
                                <Flexbox justify="space-between" align="center" gap={2} fullWidth>
                                    <span>{t('patients:patients_selected', {count: values.length})}</span>
                                </Flexbox>
                            )}
                            hasError={!!errorMessage}
                            disabled={disabled}
                        />

                        {errorMessage && (
                            <Flexbox align="center" gap={1.5}>
                                <InputStatusIcon hasError={!!errorMessage} />
                                <InputHelpText error={errorMessage} />
                            </Flexbox>
                        )}
                    </Flexbox>

                    <SelectComboboxPopover>
                        <SelectComboboxInput placeholder={t('commons:search_patient')} />
                        <SelectComboboxList>
                            {patientsQuery.isLoading ? (
                                <Flexbox direction="column" gap={2} fullWidth>
                                    {Array.from({length: 5}, (_, index) => (
                                        <Skeleton width="100%" height={32} key={index} />
                                    ))}
                                </Flexbox>
                            ) : patientsQuery.isError ? (
                                <ComboboxItem disabled>
                                    <InputHelpText error={t('errors:default')} />
                                </ComboboxItem>
                            ) : patientsQuery.remappedData.length > 0 ? (
                                <SelectVirtualList
                                    data={patientsQuery.remappedData}
                                    totalCount={patientsQuery.remappedData.length}
                                    overscan={100}
                                    endReached={() => patientsQuery.hasNextPage && patientsQuery.fetchNextPage()}
                                    itemContent={(itemIndex, item) => (
                                        <SelectItem hasItemCheckbox key={itemIndex} value={item.id}>
                                            {item.fullName}
                                        </SelectItem>
                                    )}
                                />
                            ) : (
                                <ComboboxItem disabled>
                                    <InputHelpText error={t('errors:default')} />
                                </ComboboxItem>
                            )}
                        </SelectComboboxList>
                    </SelectComboboxPopover>
                </SelectProvider>
            </ComboboxProvider>
        )
    }
)
